///
/// EarthBrowser
///
/// Created by Matt Giger on 9/16/2026
/// Copyright (c) 2026 Geodata Labs
///
/// The shared subscribe pitch — founder header, message, and yearly-plan
/// entitlements — rendered identically by the SupportInterstitial nag and
/// the SubscriptionDialog so the two surfaces never drift apart. Parents
/// own the glass container and supply the action row via `actions`.
///

import { PricingPlans } from '@shared-types/pricing-plans'
import { Globe } from 'lucide-react'
import type { ReactNode } from 'react'

// The gradient subscribe CTA style shared by every pitch surface
export const supportCtaButtonClass =
	'rounded-full shadow-lg transition-all duration-300 hover:scale-105 hover:shadow-xl ' +
	'bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white'

interface SupportPitchProps {
	// Extra bullets appended after the plan entitlements (e.g. the
	// interstitial's "No more of this window, ever")
	extraEntitlements?: string[]
	// Action row content (subscribe / dismiss buttons), rendered in the footer strip
	actions: ReactNode
	// Optional heading override so dialogs can supply an accessible DialogTitle
	heading?: ReactNode
}

export function SupportPitch({ extraEntitlements = [], actions, heading }: SupportPitchProps) {
	const plan = PricingPlans.yearly

	return (
		<>
			{/* Header */}
			<div className="flex items-center gap-3 px-6 py-4 border-b border-border/30 bg-gradient-to-r from-blue-500/5 to-cyan-500/5">
				<div className="relative">
					<div className="absolute inset-0 bg-gradient-to-br from-blue-600 via-cyan-500 to-teal-500 rounded-full blur-md opacity-75" />
					<div className="relative bg-black/40 backdrop-blur-md rounded-full p-2 border border-white/20">
						<Globe className="h-5 w-5 text-cyan-400" />
					</div>
				</div>
				<div>
					{heading ?? <h3 className="text-base font-bold text-foreground">Support EarthBrowser</h3>}
					<p className="text-xs text-muted-foreground">Built by one person since 1997</p>
				</div>
			</div>

			{/* Founder message */}
			<div className="px-6 py-5 space-y-3">
				<p className="text-sm text-foreground leading-relaxed">
					Hi — I&apos;m Matt. I shipped the original EarthBrowser as Mac shareware in
					1997, and I&apos;ve rebuilt it from scratch for the web. It&apos;s still just me,
					and subscriptions are what keep the satellites, radar, and forecasts
					flowing.
				</p>
				<ul className="text-xs text-muted-foreground list-disc pl-4 space-y-1">
					{plan.entitlements.map((entitlement) => (
						<li key={entitlement}>{entitlement}</li>
					))}
					{extraEntitlements.map((entitlement) => (
						<li key={entitlement}>{entitlement}</li>
					))}
				</ul>
			</div>

			{/* Actions */}
			<div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-border/30 bg-gradient-to-r from-purple-500/5 to-violet-500/5">
				{actions}
			</div>
		</>
	)
}
